/**
 * Camada 1 · o contrato de um produto.
 *
 * A APOSTA INTEIRA DA PLATAFORMA ESTA NESTE ARQUIVO: um produto novo e um objeto que
 * satisfaz `ProductConfig`, e nao uma pasta copiada. Enquanto isso for verdade, mudar a
 * topbar, o rail ou a tela de login altera UM lugar e reflete em todos os produtos.
 * No dia em que um produto precisar de um campo que este tipo nao tem, a pergunta certa
 * e se o campo pertence ao contrato, e nao se aquele produto merece um fork.
 *
 * O QUE NAO CABE AQUI: regra de negocio, consulta, texto de tela que muda por estado.
 * Isto e identidade e configuracao, e nada mais. Um campo que precisa ser calculado em
 * tempo de execucao esta no lugar errado.
 */

import type { TokenCor } from '@complexity/tokens';

/**
 * A cor de destaque de um produto.
 *
 * DELIBERADAMENTE NAO E UMA STRING DE COR. Aceitar '#0ea5e9' aqui abriria a porta que a
 * regra de lint fecha na cozinha: cada produto escolheria o proprio hex e a identidade
 * viraria loteria. O produto escolhe um PAPEL ja definido em tokens.css, e trocar a cor
 * daquele papel continua sendo edicao de um arquivo so.
 */
export type AccentProduto = TokenCor;

/** Um selo da tela de login: aquelas credenciais curtas que dizem por que confiar. */
export interface SeloLogin {
  /** Texto curto do selo. Ex.: "Dados no Brasil". */
  readonly texto: string;
  /** Nome do icone no conjunto de @complexity/ui. Opcional: selo sem icone e valido. */
  readonly icone?: string;
}

/** O bloco que explica o que a IA faz naquele produto, na coluna de apresentacao do login. */
export interface BlocoIALogin {
  readonly titulo: string;
  /**
   * OS USOS DA IA, UM POR ITEM, e nao um paragrafo corrido.
   *
   * O QUE COMPROU ESTE CAMPO, e a confissao esta no proprio repositorio: o commit
   * `18dea47` transcreveu os NOVE usos de IA do legado e os achatou numa frase so,
   * com o comentario do autor dizendo por que: "reunidos numa frase so porque o
   * contrato do bloco tem um campo de texto, e nao uma lista". A limitacao era
   * deste tipo, e nao do produto, e ela custou a lista que o fundador via na tela.
   *
   * ELE MANDOU EM 07/09/2026, olhando a tela de entrada publicada do VOE: "MENU DA
   * PLATAFORMA (TODOS) TERAO ESSE PADRAO". A lista de checks e o padrao, e nao uma
   * escolha por produto.
   *
   * OBRIGATORIO, E NAO OPCIONAL, de proposito. Opcional faria a falta aparecer na
   * TELA, como um cartao de IA sem conteudo, e tela vazia e descoberta por ele, o
   * que e tarde. Obrigatorio, o produto que nao declarar NAO COMPILA, e a camada
   * que garante o padrao passa a ser o typecheck em vez da lembranca de alguem.
   *
   * UM USO POR ITEM, e o item comeca com VERBO na terceira pessoa ("Traduz...",
   * "Explica...", "Resume..."), que e a forma do original recuperado. Item que
   * descreve duas coisas vira dois itens: a lista existe para ser varrida com o
   * olho, e item longo desfaz isso.
   *
   * CADA ITEM E CHAVE DO DICIONARIO de @complexity/i18n, como todo texto desta
   * casa: sem traducao cadastrada, a tela mostra o portugues, que e legivel, e nao
   * um codigo.
   */
  readonly itens: readonly string[];
  /**
   * A frase que diz o limite da IA. Obrigatoria por decisao de produto, e nao por
   * capricho: a IA sugere, organiza, resume e classifica, e nao conclui, nao assina e
   * nao pratica ato que gere consequencia para alguem fora do sistema. Se a tela de
   * login nao disser isso, nenhuma tela dira.
   */
  readonly limite: string;
}

/** Uma secao do aviso de privacidade, com titulo e corpo. */
export interface SecaoPrivacidade {
  readonly titulo: string;
  /** Um paragrafo por item, para o layout controlar o espaco entre eles. */
  readonly paragrafos: readonly string[];
}

/**
 * O aviso de privacidade, ALCANCAVEL ANTES DE ENTRAR.
 *
 * ESTE E O CAMPO MAIS IMPORTANTE DESTE ARQUIVO, e a linha L10 da paridade do prontidao.
 *
 * POR QUE ANTES E NAO DEPOIS: quem ainda nao tem conta precisa poder ler o que o sistema faz
 * com os dados dela ANTES de fornecer qualquer um. Um aviso que so existe depois do login e
 * um aviso que a pessoa le depois de ja ter entregado o e-mail, e a LGPD espera a informacao
 * antes da coleta, nao depois.
 *
 * OPCIONAL NA ASSINATURA, e essa e uma escolha consciente: produto que ainda nao escreveu o
 * proprio aviso nao ganha um link que abre uma tela vazia, que seria pior que nao ter link.
 * Mas todo produto que colete dado pessoal precisa preencher isto antes de ir ao ar.
 */
export interface PrivacidadeLogin {
  /** O rotulo do link na tela de entrada. Ex.: 'Aviso de privacidade e uso dos dados'. */
  readonly titulo: string;
  /**
   * A VERSAO DESTE TEXTO, e ela e o que faz o aceite ser prova.
   *
   * Somada em 31/08/2026, quando o aceite de termos passou a existir. Decisao dele por
   * enquete: se o documento mudar, quem ja aceitou ve a tela de novo, e o banco guarda as
   * DUAS linhas com as duas datas.
   *
   * SEM ESTE CAMPO O ACEITE MENTE. Ele registraria "esta pessoa aceitou a privacidade", e
   * uma clausula alterada depois passaria a valer para quem nunca a leu. Com a versao, o
   * registro diz "aceitou a versao 1", e a versao 2 volta a ser perguntada.
   *
   * QUEM MUDA O NUMERO E QUEM MUDA O TEXTO, no mesmo commit. Alterar paragrafo sem subir a
   * versao e o unico jeito de quebrar isto, e por isso a regra fica escrita aqui, ao lado
   * do campo, e nao so num documento que ninguem abre ao editar um paragrafo.
   *
   * Texto livre e nao numero: `'1'`, `'2026-08'` ou `'1.2'` servem. O que o sistema exige e
   * que ele MUDE quando o texto mudar, e a comparacao e de igualdade exata.
   */
  readonly versao: string;
  /**
   * OPCIONAL: o rotulo curto do SELO, quando o produto quer o tratamento de selo em vez do
   * link discreto. Linha L11 da paridade do prontidao, onde o legado escreve "Seus dados e a
   * LGPD" ao lado de um escudo, na cor da marca, e nao o titulo longo do aviso.
   *
   * E opcional de proposito: produto que nao declarar continua com o link discreto de hoje,
   * exatamente como esta. Um campo obrigatorio aqui obrigaria os produtos e o _template a
   * decidir agora uma coisa que ninguem pediu, e mudaria a tela deles sem decisao dele.
   *
   * O titulo continua sendo o do MODAL. No legado sao dois textos diferentes de proposito:
   * o botao diz o que a pessoa ganha ao clicar, e o modal diz o que ela esta lendo.
   */
  readonly selo?: string;
  readonly secoes: readonly SecaoPrivacidade[];
}

/** A coluna de apresentacao da tela de login, que e o que diferencia um produto do outro. */
export interface LoginProduto {
  readonly titulo: string;
  readonly subtitulo: string;
  /**
   * As palavras do `subtitulo` que ganham PESO, e nada alem delas.
   *
   * ELE MANDOU EM 08/09/2026, com a captura da tela publicada: "Desde o **amador** ao voo
   * **executivo**" sai com as duas palavras em negrito, porque sao elas que dizem o alcance
   * do produto. Sem o peso, a frase vira uma linha cinza que ninguem le.
   *
   * DECLARADA, E NAO MARKDOWN INTERPRETADO, e a razao e dupla. Um parser de `**texto**` na
   * tela deixaria o config injetar marcacao arbitraria numa superficie que nao valida nada;
   * e quebraria a chave do dicionario de @complexity/i18n, que e a FRASE EM PORTUGUES: com
   * asterisco no meio, a chave deixa de casar e a traducao some sem erro nenhum.
   *
   * OPCIONAL de proposito: produto que nao declarar desenha o subtitulo inteiro no mesmo
   * peso, exatamente como hoje, e nenhuma tela muda sem alguem decidir.
   *
   * PALAVRA QUE NAO APARECE no subtitulo simplesmente nao pinta nada. A alternativa seria
   * quebrar a tela por causa de uma palavra mal escrita num config, e tela de entrada que
   * nao abre e pior que uma enfase que faltou.
   */
  readonly enfasesDoSubtitulo?: readonly string[];
  /** Um paragrafo por item. Lista e nao texto unico para o layout controlar o espaco. */
  readonly paragrafos: readonly string[];
  /** Opcional: produto sem IA nao desenha o bloco, em vez de desenha-lo vazio. */
  readonly blocoIA?: BlocoIALogin;
  readonly selos: readonly SeloLogin[];
  /** O aviso de privacidade, alcancavel ANTES de entrar. Ver `PrivacidadeLogin`. */
  readonly privacidade?: PrivacidadeLogin;
  /**
   * EM PRODUCAO, ESTE PRODUTO OFERECE APENAS O GOOGLE.
   *
   * PEDIDO DO FUNDADOR EM 13/09/2026, com a captura da tela do Super Admin: "nessa tela
   * havera apenas login com o google". E o recorte, fechado por ele em seguida: "para o
   * admin, no medicina ficam as duas formas de login", e "deixe as duas formas de entrada
   * (google apenas somente em producao)".
   *
   * SAO DUAS ORDENS QUE CONVIVEM, e esta declaracao e o que as concilia. O admin da
   * plataforma nao tem usuario comum: quem entra ali e operador, e operador tem conta
   * Google. Ao mesmo tempo, a conta de trabalho `cds@cds` precisa continuar entrando em
   * local e em hml por regra dura, e sem o campo ela nao teria por onde.
   *
   * O AMBIENTE NAO E DECIDIDO AQUI, e isso e deliberado: o produto DECLARA a intencao, e
   * quem decidia os ambientes da senha era a conta de trabalho da plataforma, que saiu deste repositorio; no
   * `@complexity/auth`. Se a decisao morasse no config, cada produto poderia escolher um
   * conjunto diferente de ambientes, e a familia deixaria de parecer uma familia.
   *
   * OPCIONAL, E O PADRAO E FALSO: produto que nao declarar continua com as duas formas em
   * todo ambiente, exatamente como hoje, e nenhuma tela muda sem alguem decidir.
   */
  readonly somenteGoogleEmProducao?: boolean;
}

/**
 * A secao a que um item do menu pertence.
 *
 * NAO E ENFEITE, e a linha V04 da paridade do prontidao. O legado separa os doze modulos da
 * OPERACAO dos dois itens que sao o PROPRIO SISTEMA (usuarios e chaves de acesso) por uma
 * divisoria, e sem ela "Chaves de acesso" parece um modulo da operacao: o piloto procura voo
 * dentro dele.
 *
 * Tipo fechado, e nao string livre: com string, cada produto inventaria o proprio nome de
 * secao e a divisoria apareceria em lugares diferentes em cada um. O que se ganha aqui e a
 * familia de produtos parecer uma familia.
 */
export type SecaoMenu = 'operacao' | 'sistema';

/**
 * O ROTULO de cada secao, que o rail e a gaveta desenham como cabecalho do grupo.
 *
 * POR QUE ELE EXISTE, e o padrao tem dono: o Primer, do GitHub, resolve lista longa de
 * navegacao com `NavList.Group` mais cabecalho, e o cabecalho nomeia a regiao para quem usa
 * leitor de tela. Ate 06/09/2026 a troca de secao aqui desenhava APENAS a divisoria, e
 * divisoria sozinha e espaco em branco para quem nao enxerga: a informacao "daqui para baixo
 * e outro assunto" chegava so a quem ve a linha.
 *
 * O NUMERO QUE COMPROU A MUDANCA: o VOE tem 14 destinos, outro produto tinha 12 e o admin 8, contra
 * os 3 a 7 que o Material 3 recomenda. Com 14 itens iguais, quem procura le todos ate achar.
 *
 * E O TIPO CONTINUA FECHADO, de proposito: com secao livre, cada produto inventaria o proprio
 * nome e a divisoria apareceria em lugar diferente em cada um. O que se ganha mantendo fechado
 * e a familia de produtos parecer uma familia, e isso vale mais que a granularidade que um
 * produto especifico gostaria de ter.
 *
 * O ROTULO E A CHAVE do dicionario de @complexity/i18n, como todo texto desta casa: sem
 * traducao cadastrada, a tela mostra o portugues, que e legivel, e nao um codigo.
 */
export const ROTULO_SECAO: Record<SecaoMenu, string> = {
  operacao: 'Operação',
  sistema: 'Sistema',
};

/** Um item do menu do rail lateral. */
export interface ItemMenu {
  /**
   * Nome do icone no conjunto de @complexity/ui, resolvido pelo mapa `ICONES`.
   *
   * NOME, E NAO O DESENHO. Nome desconhecido faz o rail desenhar um ponto no lugar, e nao um
   * espaco vazio: item de menu sem nada a esquerda desalinha a lista inteira e le como
   * defeito de carregamento.
   */
  readonly icone: string;
  /** Rota relativa a raiz do produto. Ex.: '/pacientes'. */
  readonly rota: string;
  /**
   * O rotulo EM PORTUGUES, que e tambem a chave do dicionario de @complexity/i18n.
   * Sem traducao cadastrada, a tela mostra este texto, que e legivel, em vez de um codigo.
   */
  readonly label: string;
  /** Permissoes que o item exige. Vazio ou ausente = visivel para todo membro do tenant. */
  readonly permissoes?: readonly string[];

  /**
   * O item em destaque no menu. Linha V02 da paridade do prontidao.
   *
   * UM SO POR PRODUTO, e o loader recusa dois: marcar mais de um anula o destaque, que e o
   * ponto inteiro dele. No legado o item destacado e `Voar`, porque "e o gesto que a operacao
   * faz todo dia, e por onde o voo sai do planejamento e vira registro".
   */
  readonly destaque?: boolean;

  /**
   * A secao do item. Ausente vale `operacao`, que e onde a esmagadora maioria dos itens mora.
   *
   * A DIVISORIA E DESENHADA NA TROCA de secao, e nao por um campo separado de "desenhar
   * divisoria aqui": duas fontes para a mesma decisao divergem, e a que fica errada e a que
   * ninguem olha.
   */
  readonly secao?: SecaoMenu;

  /**
   * O item aparece na barra inferior do celular. Linha V22 da paridade do prontidao.
   *
   * NO MAXIMO QUATRO, e o loader recusa o quinto. O numero nao e arbitrario: com cinco, cada
   * alvo cai abaixo dos 44px em 390px de largura, e a barra passa a ser uma fileira de botoes
   * que ninguem acerta. O legado escolheu quatro pelo mesmo motivo, e ate registrou qual item
   * saiu quando `Voar` entrou.
   *
   * Produto que nao declara nenhum simplesmente nao tem barra inferior, e nao ganha uma barra
   * vazia ocupando altura no telefone.
   */
  readonly naBarraInferior?: boolean;

  /**
   * O item vem DEPOIS da Ajuda que o chassi acrescenta ao menu.
   *
   * AUSENTE, o item fica antes da Ajuda, que é onde todo item de todo produto sempre ficou. A
   * Ajuda entra antes do primeiro item marcado, e os marcados seguem na ordem do array.
   *
   * NASCEU EM 12/09/2026 no Medicina, por pedido do fundador: "No menu Sobre vem abaixo de
   * ajuda". Quem acrescenta a Ajuda é o chassi (packages/chassi/src/Chassi.tsx), então o
   * produto não tinha como pôr um item depois dela.
   */
  readonly depoisDaAjuda?: boolean;
}

/** Ligacao do produto com a base de conhecimento e o painel de ajuda. */
export interface AjudaProduto {
  /**
   * O recorte da base de conhecimento deste produto. E o mesmo valor que services/knowledge
   * usa para separar o conteudo, entao dois produtos com o mesmo namespace passam a ver a
   * ajuda um do outro, o que e defeito e nao recurso.
   */
  readonly namespace: string;
  /**
   * O ÍCONE DE AJUDA APARECE NO CABEÇALHO? Padrão: sim.
   *
   * POR QUE ELE EXISTE, pedido do fundador em 07/10/2026 sobre o app da cliente: *"tirar botao de
   * ajuda da tela da cliente (em todos os dispositivos)"*. Até aqui o ícone era desenhado pela
   * `Topbar` sem interruptor nenhum, e o manifesto da cliente já registrava isso como pendência:
   * a ajuda estava desligada por todos os outros caminhos (item de menu escondido, rota ignorada
   * pelo app), e só o ícone continuava na tela, levando a lugar nenhum.
   *
   * `false` tira o ícone em TODA largura, e não só no celular: botão que não faz nada é pior em
   * qualquer tela, e esconder por largura deixaria o defeito vivo onde ninguém olha.
   */
  readonly noCabecalho?: boolean;
  /**
   * QUEM DESENHA A TELA DE AJUDA deste produto.
   *
   *   `central` (o padrao) · a Central de Ajuda do chassi, com busca, categorias e as
   *                          solicitacoes. E o que todo produto de cliente usa.
   *   `propria`            · o proprio app atende a rota `/ajuda`, e o chassi devolve
   *                          `children`. Hoje so o `admin`.
   *
   * POR QUE O ADMIN E DIFERENTE, e a decisao e dele em 07/09/2026: a base de conhecimento
   * e o conteudo que a operacao PUBLICA para os clientes, e o Super Admin e quem publica.
   * Oferecer a ele uma busca no proprio material seria devolver o que ele mesmo escreveu.
   * A ajuda de que ele precisa e outra: o que cada item do menu faz.
   *
   * OPCIONAL DE PROPOSITO: ausente vale `central`, entao nenhum produto existente muda.
   */
  readonly tela?: 'central' | 'propria';
}

/**
 * Ligacao do produto com a conversa e o sino.
 *
 * POR QUE ISTO E DECLARACAO E NAO CODIGO: medido em 05/09/2026, o `admin` tinha uma segunda
 * implementacao do sino, com 80 linhas, porque o operador do plano de controle NAO TEM tenant
 * e o transporte padrao exige `X-Tenant-Id`. A diferenca e real e nao some. O que ela nao pode
 * ser e improvisada dentro de cada app: declarada aqui, o codigo continua sendo um so e a
 * escolha fica a vista no manifesto.
 */
export interface MensagensProduto {
  /**
   * De que lado deste produto esta quem usa: quem PEDE ajuda, ou quem RESPONDE.
   *
   * NAO E ROTULO: decide o que o sino conta. Mensagem da propria operacao nunca conta como
   * nao lida para ela (`contarNaoLidas` em packages/chat/src/index.ts:98), entao sem este
   * campo o contador do admin somaria as proprias respostas.
   */
  readonly lado: 'cliente' | 'operacao';

  /**
   * Em que plano as conversas vivem.
   *
   * `tenant` fala com services/messaging e exige X-Tenant-Id. `control-plane` fala com o
   * admin-api e NAO manda tenant, porque o operador age sobre todos os clientes: inventar um
   * valor de fachada ali toma 401 e deixa o sino indisponivel para sempre.
   */
  readonly plano: 'tenant' | 'control-plane';
}

/** Configuracao de IA do produto. */
export interface IAProduto {
  /** Desligado, o produto nao desenha nenhuma superficie de IA. Nao e so esconder botao. */
  readonly habilitada: boolean;
  /**
   * Teto mensal de token do produto por tenant. Zero significa sem teto proprio, caindo
   * no teto do plano. TODA chamada passa por services/ai-gateway, que e quem mede e corta:
   * numero aqui sem o gateway na frente e numero que ninguem cumpre.
   */
  readonly cotaMensalTokens: number;
}

/**
 * O contrato completo de um produto.
 *
 * `readonly` em tudo de proposito: a configuracao e lida em dezenas de lugares e nunca
 * escrita. Deixa-la mutavel convidaria alguem a ajustar um campo em tempo de execucao
 * para resolver um caso especifico, e a partir dai a configuracao deixa de ser a verdade.
 */
export interface ProductConfig {
  /**
   * Identificador estavel, em minusculas e sem espaco. Entra em rota, em chave de
   * armazenamento e em registro de auditoria, entao trocar depois quebra historico.
   */
  readonly id: string;

  /**
   * O nome CURTO, como a pessoa le no menu e na barra do topo.
   *
   * CURTO NAO E ESTILO, E MEDIDA: o rail expandido tem 240px (`w-60` em Rail.tsx:213), e
   * depois do simbolo, do botao de colapsar e dos paddings sobram cerca de 140. Um nome que
   * passe disso e cortado pelo `truncate` da linha 167, e o corte aparece para quem usa.
   */
  readonly nome: string;

  /**
   * O nome POR EXTENSO, para as superficies onde ha espaco de sobra. Hoje, a tela de
   * entrada.
   *
   * OPCIONAL, E AUSENTE DEGRADA PARA `nome`. Isso e o que permite o campo nascer sem
   * obrigar os outros produtos a declarar coisa nenhuma: quem nao tem nome longo continua
   * com um nome so, e nada muda para ele.
   *
   * POR QUE DOIS CAMPOS E NAO UM, e a razao veio da tela dele em 07/09/2026: no menu o nome
   * comprido CORTAVA, e no login sobrava espaco. Um campo unico obrigaria a escolher entre
   * cortar num lugar e comunicar menos no outro. As palavras dele foram "NO MENU APENAS
   * MEDICINA E NO LOGIN MEDICINA CONCIERGE".
   */
  readonly nomeCompleto?: string;

  /** Uma ou duas letras para o avatar quadrado do rail, onde o nome inteiro nao cabe. */
  readonly iniciais: string;

  /** O papel de cor deste produto, escolhido entre os tokens existentes. */
  readonly accent: AccentProduto;

  readonly login: LoginProduto;

  /** Ordem do array e a ordem na tela. Nao ha campo de ordenacao de proposito: duas
   *  fontes de ordem sempre divergem, e a que fica errada e a que ninguem olha. */
  readonly menu: readonly ItemMenu[];

  readonly ajuda: AjudaProduto;

  /**
   * OBRIGATORIO DESDE 06/09/2026, e o `?` saiu porque os QUATRO manifestos declararam.
   *
   * A ORDEM FOI ACRESCIMO PRIMEIRO, RESTRICAO POR ULTIMO, como o comentario anterior
   * previa: obrigatorio antes disso deixaria manifesto sem compilar e o repositorio ficaria
   * quebrado entre dois passos. Medido no dia em que a tranca fechou: os produtos, admin,
   * _template e voe, os quatro declarando.
   *
   * O QUE ELA IMPEDE, e nao e teorico: enquanto era opcional, o chassi caia num PADRAO
   * SILENCIOSO (`{ lado: 'cliente', plano: 'tenant' }`) para quem nao declarasse. Num app
   * de plano de controle esse padrao esta errado, e o sintoma seria o sino somando as
   * proprias respostas do operador como nao lidas, ou 401 sem ninguem entender por que.
   *
   * AGORA O APP NOVO NAO TEM COMO ESQUECER: ou declara, ou nao compila. E a diferenca entre
   * uma regra que alguem lembra numa revisao e uma que o typecheck recusa.
   */
  readonly mensagens: MensagensProduto;

  readonly ia: IAProduto;

  /**
   * Para onde o clique na MARCA do rail leva. Linha V26 da paridade do prontidao.
   *
   * No legado a tela "Sobre o sistema" NAO e item de menu: ela e alcancada pela marca, no topo
   * do menu lateral. Reproduzir isso exige o chassi saber para onde a marca aponta, e o campo
   * existe so para isso.
   *
   * AUSENTE, A MARCA NAO E CLICAVEL, e essa e a escolha certa: marca que parece botao e nao
   * navega e affordance mentirosa, e custa mais que marca que nao parece botao. A rota
   * declarada aqui NAO precisa estar no menu, e e justamente esse o caso de uso.
   */
  readonly rotaDaMarca?: string;

  /**
   * O NOME FALADO DO SINO do cabeçalho, quando o produto quer um nome próprio.
   *
   * AUSENTE, o sino continua se chamando "Avisos", e "Avisos: N" quando há não lidas, que é o
   * nome do VOE, do admin e do _template. PRESENTE, o chassi fala o nome declarado e acrescenta
   * "1 nova" ou "N novas" (packages/chassi/src/Chassi.tsx).
   *
   * NASCEU EM 11/09/2026 no Medicina, por decisão do fundador em enquete: lá o número conta
   * respostas da equipe de suporte ainda não lidas e o clique abre a Central de Ajuda, e
   * "Avisos" prometia alertas do sistema. O nome é por produto porque o destino do sino
   * também é (o admin leva a Mensagens).
   */
  readonly rotuloDoSino?: string;

  /**
   * O DESENHO DO CABEÇALHO DO MENU, quando o produto quer um próprio.
   *
   * AUSENTE, o cabeçalho continua como sempre foi: símbolo de 36px, o `nome` curto, a marca
   * navegando para `rotaDaMarca` e o controle de recolher como botão separado à direita. É o
   * desenho do VOE, do admin e do _template, e o retrato congelado do VOE depende dele.
   *
   * NASCEU EM 12/09/2026 no Medicina, pedido do fundador olhando o menu: "diminuir a logo e
   * colocar Medicina Concierge, tb ao clicar em qualquer lugar da imagem vai retrair o menu".
   * E na enquete do mesmo dia: "TUDO O QUE ESTAMOS FAZENDO AQUI VIRA CHASSI. MAS AQUI NESSA
   * SESSAO SO SE MEXE NO MEDICINA". Por isso é capacidade do chassi, ligada só por quem declara.
   */
  readonly cabecalhoDoMenu?: CabecalhoDoMenu;

  /**
   * A ASSINATURA DA PLATAFORMA ("Complexity Digital Systems") no pé do menu.
   *
   * AUSENTE, VALE `true`: ela fica no menu, como no VOE, no admin e no _template. `false` a tira
   * do menu, e o produto a mostra onde decidir com a peça `AssinaturaDaPlataforma` de
   * `@complexity/brand`. Nasceu em 12/09/2026 no Medicina, que a levou para a tela Sobre, na
   * linha do título, à direita, por pedido do fundador.
   */
  readonly assinaturaNoMenu?: boolean;

  /**
   * O SAIR MORA DENTRO DA CONTA, e não solto no pé do menu.
   *
   * AUSENTE, o Sair continua visível no menu, acima da conta, como estão o VOE, o admin e o
   * _template. LIGADO, a conta vira um botão que abre um painel com os dados da pessoa que o
   * produto passa ao Chassi (`dadosDaConta`) e o Sair logo abaixo deles.
   *
   * NASCEU EM 12/09/2026 no Medicina, pedido do fundador: "o Sair sai do menu e vai para dentro
   * quando clicar em conta da medica (tb ali trazer os dados da profissional) Nome completo e
   * CRM".
   */
  readonly sairNaConta?: boolean;
}

/**
 * O cabeçalho do menu lateral. Cada campo ausente mantém o desenho de sempre.
 */
export interface CabecalhoDoMenu {
  /**
   * O cabeçalho escreve `nomeCompleto` em vez de `nome`. Sem `nomeCompleto`, vale `nome`.
   *
   * SÓ CABE COM OS OUTROS DOIS CAMPOS LIGADOS, e a conta é de largura: o rail expandido tem
   * 240px, e é o símbolo menor mais o ícone de recolher dentro do mesmo botão que devolvem ao
   * nome o espaço que o botão separado de 44px tomava. A suíte `qa-plt-menu` mede o nome sem
   * corte na tela.
   */
  readonly nomePorExtenso?: boolean;

  /** O símbolo da marca com 28px, em vez de 36px. */
  readonly simboloCompacto?: boolean;

  /**
   * O cabeçalho INTEIRO vira o controle do menu, e não só o ícone da direita: no computador
   * recolhe e expande, na gaveta do celular fecha a gaveta, e no tablet, onde o menu é sempre
   * tira, ele volta a ser só identidade, porque botão que não faz nada é affordance mentirosa.
   *
   * LIGADO, `rotaDaMarca` NÃO SE APLICA: o mesmo clique não pode navegar e recolher.
   *
   * RECOLHIDO, FICA SÓ O ÍCONE DE EXPANDIR, sem o símbolo, e nada muda de lugar: a linha do
   * cabeçalho e as dos grupos guardam a altura, e o rodapé não recentraliza. Pedido do fundador
   * em 12/09/2026: "com o menu retraído o C sai, fica apenas o ícone de expandir (não ficar
   * deslocando a posição dos ícones fechado o expandido)".
   */
  readonly recolheAoClicar?: boolean;
}

/**
 * Erro de configuracao invalida. Tipo proprio para o shell conseguir distinguir
 * "o produto esta mal configurado" de qualquer outra falha e mostrar a mensagem certa.
 */
export class ProductConfigInvalida extends Error {
  constructor(
    readonly produtoId: string,
    readonly problemas: readonly string[],
  ) {
    super(`ProductConfig invalida para "${produtoId}": ${problemas.join('; ')}`);
    this.name = 'ProductConfigInvalida';
  }
}
